/**
 * BasicInfoStep — Step 1 of the Rodeo Wizard
 *
 * Captures: name, organization, dates, location, description, cash payment
 * and Pay Later toggles, the membership-requirement override (org default
 * with a per-rodeo override), and non-member surcharge. Association was
 * removed from the form
 * per product direction — the legacy `basicInfo.association` field is kept
 * on the store for back-compat but is no longer user-editable here.
 */

import { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  useRodeoStore,
  type PreferenceOption,
} from '@/state_management_and_stores/rodeo.store';
import { useUserOrganizations } from '@/api/extensions/useJackpotOps';
import {
  US_TIMEZONES,
  getTimezoneAbbreviation,
  type UsTimezone,
} from '@/utils/timezone';

const PREFERENCE_OPTIONS: { value: PreferenceOption; label: string }[] = [
  { value: 'performance', label: 'Performance' },
  { value: 'slack', label: 'Slack' },
  { value: 'out', label: 'Out' },
];

export function BasicInfoStep() {
  const basicInfo = useRodeoStore((s) => s.basicInfo);
  const setBasicInfo = useRodeoStore((s) => s.setBasicInfo);
  const { data: orgs } = useUserOrganizations();

  // When the user admins exactly one organization, skip the dropdown and
  // render a disabled input with the org name. Auto-populate the store's
  // organization_id so "Save" isn't blocked by a missing-field warning.
  const orgList: any[] = Array.isArray(orgs) ? orgs : [];
  const isSingleOrg = orgList.length === 1;
  const soleOrg = isSingleOrg ? orgList[0] : null;

  useEffect(() => {
    if (isSingleOrg && soleOrg && !basicInfo.organization_id) {
      setBasicInfo({ organization_id: soleOrg.id });
    }
  }, [isSingleOrg, soleOrg, basicInfo.organization_id, setBasicInfo]);

  // Membership requirement resolves org default -> rodeo override, mirroring
  // the server-side resolver. `null` on the rodeo means inherit; we surface
  // the org's value in the picker so "Use organization default" isn't opaque.
  const selectedOrg =
    orgList.find((o) => o.id === basicInfo.organization_id) ?? soleOrg;
  const orgDefaultMembershipRequired: boolean | null =
    selectedOrg && typeof selectedOrg.member_fee_enabled === 'boolean'
      ? selectedOrg.member_fee_enabled
      : null;
  const membershipRequired =
    basicInfo.member_fee_enabled ?? orgDefaultMembershipRequired ?? true;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Basic Information</h2>
        <p className="text-sm text-muted-foreground">
          Enter the core details for your rodeo.
        </p>
      </div>

      <div className="space-y-4">
        {/* Name */}
        <div className="space-y-1.5">
          <Label htmlFor="rodeo-name">Rodeo Name *</Label>
          <Input
            id="rodeo-name"
            value={basicInfo.name}
            onChange={(e) => setBasicInfo({ name: e.target.value })}
            placeholder="e.g., Spring Championship Rodeo"
          />
        </div>

        {/* Organization — read-only when the user admins a single org,
            dropdown otherwise. Association was removed per product spec. */}
        <div className="space-y-1.5">
          <Label>Organization</Label>
          {isSingleOrg ? (
            <Input
              value={soleOrg?.name ?? ''}
              readOnly
              disabled
              className="bg-muted/40 cursor-not-allowed"
            />
          ) : (
            <Select
              value={basicInfo.organization_id}
              onValueChange={(v) => setBasicInfo({ organization_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {orgList.map((org: any) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="start-date">Start Date</Label>
            <Input
              id="start-date"
              type="date"
              value={basicInfo.start_date}
              onChange={(e) => setBasicInfo({ start_date: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="end-date">End Date</Label>
            <Input
              id="end-date"
              type="date"
              value={basicInfo.end_date}
              onChange={(e) => setBasicInfo({ end_date: e.target.value })}
            />
          </div>
        </div>

        {/* Timezone — anchors every wall-clock time on the rodeo (entry
            close, performance start) so the same input renders identically
            for every viewer. Defaults to the browser's TZ mapped to the
            nearest US zone; the admin can override before saving. */}
        <div className="space-y-1.5">
          <Label htmlFor="rodeo-timezone">Timezone *</Label>
          <Select
            value={basicInfo.timezone}
            onValueChange={(v) =>
              setBasicInfo({ timezone: v as UsTimezone })
            }
          >
            <SelectTrigger id="rodeo-timezone">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {US_TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Every time on this rodeo (entry close, performance start) is
            displayed in this timezone with the abbreviation appended.
          </p>
        </div>

        {/* Entry Close Time + Fees Due Message — both surface on the
            registration wizard's payment step. entry_close_time is
            required; the wizard's Save button is disabled when it's blank. */}
        <div className="space-y-1.5">
          <Label htmlFor="entry-close-time">
            Entry Close Time{' '}
            <span className="text-muted-foreground font-normal">
              ({getTimezoneAbbreviation(basicInfo.timezone)})
            </span>{' '}
            *
          </Label>
          <Input
            id="entry-close-time"
            type="datetime-local"
            value={basicInfo.entry_close_time}
            onChange={(e) => setBasicInfo({ entry_close_time: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            After this time, entries are no longer accepted. Shown to
            contestants on the payment step of entry.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="fees-due-message">Fees Due Message</Label>
          <Input
            id="fees-due-message"
            type="text"
            maxLength={255}
            value={basicInfo.fees_due_message}
            // Strip newlines so a single-line note never wraps in the UI.
            onChange={(e) =>
              setBasicInfo({ fees_due_message: e.target.value.replace(/[\r\n]+/g, ' ') })
            }
            placeholder="fees due 1 week after entries close"
          />
          <p className="text-xs text-muted-foreground">
            Optional. Leave blank to show only the entry close time.
          </p>
        </div>

        {/* Location */}
        <div className="space-y-1.5">
          <Label htmlFor="location">Location</Label>
          <Input
            id="location"
            value={basicInfo.location}
            onChange={(e) => setBasicInfo({ location: e.target.value })}
            placeholder="e.g., Fort Worth Stockyards, TX"
          />
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={basicInfo.description}
            onChange={(e) => setBasicInfo({ description: e.target.value })}
            placeholder="Optional description or notes"
            rows={3}
          />
        </div>

        {/* Cash Payment + Non-member surcharge */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t">
          <div className="flex items-center gap-3">
            <Switch
              id="allow-cash-payment"
              checked={basicInfo.allow_cash_payment}
              onCheckedChange={(v) => setBasicInfo({ allow_cash_payment: v })}
            />
            <div>
              <Label htmlFor="allow-cash-payment">Allow Cash Payment</Label>
              <p className="text-xs text-muted-foreground">
                Let contestants pay with cash at the gate
              </p>
            </div>
          </div>
          {/* Pay Later — sibling of the cash toggle. Whether it's members-only
              follows the membership requirement resolved just below, so the
              helper text spells out which way it currently lands. */}
          <div className="flex items-center gap-3">
            <Switch
              id="allow-pay-later"
              checked={basicInfo.allow_pay_later}
              onCheckedChange={(v) => setBasicInfo({ allow_pay_later: v })}
            />
            <div>
              <Label htmlFor="allow-pay-later">Allow Pay Later</Label>
              <p className="text-xs text-muted-foreground">
                {membershipRequired
                  ? 'Members can settle online after the draw (members only while membership is required)'
                  : 'Anyone can settle online after the draw'}
              </p>
            </div>
          </div>
          {/* Finals rodeo — structural, not a payment knob, but it lives in
              this same toggle grid so the producer sets it while filling in
              the rest of the basics. */}
          <div className="flex items-center gap-3">
            <Switch
              id="is-finals"
              checked={basicInfo.is_finals}
              onCheckedChange={(v) => setBasicInfo({ is_finals: v })}
            />
            <div>
              <Label htmlFor="is-finals">This is a finals rodeo</Label>
              <p className="text-xs text-muted-foreground">
                Every contestant runs in every performance their event's rounds
                are tied to. Buddy groups and performance preferences are
                turned off, and the draw uses the finals run order.
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="non-member-surcharge">
              Non-Member Surcharge
            </Label>
            <CurrencyInput
              id="non-member-surcharge"
              value={
                basicInfo.non_member_surcharge != null
                  ? String(basicInfo.non_member_surcharge)
                  : ''
              }
              onChange={(v) => {
                const num = parseFloat(v);
                setBasicInfo({ non_member_surcharge: Number.isNaN(num) ? 0 : num });
              }}
              disabled={!membershipRequired}
            />
            {!membershipRequired && (
              <p className="text-xs text-muted-foreground">
                Not charged — this rodeo doesn't require membership.
              </p>
            )}
          </div>
          {/* Membership requirement: org default with a per-rodeo override.
              Tri-state on purpose — "Use organization default" is not the same
              as explicitly requiring or not requiring membership, and picking
              it back clears the override so future org changes flow through. */}
          <div className="space-y-1.5">
            <Label htmlFor="membership-required">Membership Required</Label>
            <Select
              value={
                basicInfo.member_fee_enabled === null
                  ? 'inherit'
                  : basicInfo.member_fee_enabled
                    ? 'required'
                    : 'not_required'
              }
              onValueChange={(v) =>
                setBasicInfo({
                  member_fee_enabled:
                    v === 'inherit' ? null : v === 'required',
                })
              }
            >
              <SelectTrigger id="membership-required">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">
                  Use organization default
                  {orgDefaultMembershipRequired != null
                    ? ` (${orgDefaultMembershipRequired ? 'required' : 'not required'})`
                    : ''}
                </SelectItem>
                <SelectItem value="required">Required</SelectItem>
                <SelectItem value="not_required">Not required</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              When membership isn't required, everyone enters as a member: no
              non-member surcharge, no membership prompt at entry, and Cash /
              Pay Later are open to all contestants.
            </p>
          </div>
        </div>

        {/* 2nd Performance Preference rules — admin/delegate-controlled */}
        <PreferenceConfigSection />
      </div>
    </div>
  );
}

/**
 * Two checkbox sections that gate which 2nd-preference options contestants
 * can pick based on their 1st choice. Each section persists its selections
 * onto basicInfo.preference_config via the rodeo store; the store sends the
 * payload up on every saveDraft.
 */
function PreferenceConfigSection() {
  const basicInfo = useRodeoStore((s) => s.basicInfo);
  const setBasicInfo = useRodeoStore((s) => s.setBasicInfo);
  const config = basicInfo.preference_config;

  const toggle = (
    branch: 'ifPerformance' | 'ifSlack',
    option: PreferenceOption,
    checked: boolean,
  ) => {
    const current = config[branch];
    const next = checked
      ? Array.from(new Set([...current, option]))
      : current.filter((o) => o !== option);
    setBasicInfo({
      preference_config: {
        ...config,
        [branch]: next,
      },
    });
  };

  return (
    <div className="space-y-4 pt-2 border-t">
      <div>
        <Label>2nd Preference Rules</Label>
        <p className="text-xs text-muted-foreground">
          Pick which 2nd-preference options contestants may choose, based on
          what they pick first. The contestant&apos;s exact 1st pick is
          automatically excluded from the 2nd-choice list to prevent
          duplicates, but other performances in the same category remain
          selectable when &quot;performance&quot; is checked.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <PreferenceBranchPicker
          title="If 1st preference is Performance"
          branch="ifPerformance"
          selected={config.ifPerformance}
          onToggle={(opt, checked) => toggle('ifPerformance', opt, checked)}
        />
        <PreferenceBranchPicker
          title="If 1st preference is Slack"
          branch="ifSlack"
          selected={config.ifSlack}
          onToggle={(opt, checked) => toggle('ifSlack', opt, checked)}
        />
      </div>
    </div>
  );
}

interface PreferenceBranchPickerProps {
  title: string;
  branch: 'ifPerformance' | 'ifSlack';
  selected: PreferenceOption[];
  onToggle: (option: PreferenceOption, checked: boolean) => void;
}

function PreferenceBranchPicker({
  title,
  branch,
  selected,
  onToggle,
}: PreferenceBranchPickerProps) {
  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="text-sm font-medium">{title}</div>
      <div className="space-y-1.5">
        {PREFERENCE_OPTIONS.map((opt) => {
          const id = `pref-${branch}-${opt.value}`;
          const checked = selected.includes(opt.value);
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className="flex items-center gap-2 text-sm cursor-pointer"
            >
              <input
                id={id}
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={checked}
                onChange={(e) => onToggle(opt.value, e.target.checked)}
              />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
